import { ServerMessagePayload } from '@common/api';
import { Container } from 'pixi.js';

import { ClientRegistry, PixiSpriteData } from '../components';
import {
  ClientFilteredComponentSystem,
  FilteredClientComponentSystemUpdateEntityArgs,
} from './ClientBase';

export class RenderSystem extends ClientFilteredComponentSystem {
  public componentClasses: (keyof ClientRegistry)[] = [
    'VisualPosition',
    'Sprite',
    'GlobalTransform',
  ];

  updateEntity({
    world,
    entityId,
  }: FilteredClientComponentSystemUpdateEntityArgs): ServerMessagePayload[] {
    const spriteComp = world.getComponent(entityId, 'Sprite')!;
    const globalTransform = world.getComponent(entityId, 'GlobalTransform')!;
    const pos = world.getComponent(entityId, 'VisualPosition')!;

    let pixiSprite = world.getComponent(entityId, 'PixiSprite');
    const renderService = this.clientServices.render;
    const pixiInstanceService = this.clientServices.pixiInstance;
    const stage = renderService.stage;

    if (!pixiSprite && stage) {
      // Create Container and store in PixiInstanceService
      const sprite = new Container();
      sprite.label = `entity_${entityId}`;

      // Enable click detection
      // Deferred because PixiJS events fire async from game loop.
      sprite.eventMode = 'static';
      sprite.cursor = 'pointer';
      sprite.on('pointerdown', () => {
        this.clientServices.common.eventBus.addDeferredEvent({
          name: 'EntityClicked',
          source: 'RenderSystem',
          payload: {
            clientEntityId: entityId,
          },
        });
      });

      stage.addChild(sprite);
      pixiInstanceService.setSprite(entityId, sprite);

      const pixiSpriteData: PixiSpriteData = {
        initialized: true,
        lastTextureId: '',
      };
      pixiSprite = world.createComponent('PixiSprite', pixiSpriteData);
      this.addComponent(entityId, pixiSprite);
    }

    const sprite = pixiInstanceService.getSprite(entityId);
    if (pixiSprite && sprite) {
      // Use RepresentationService to sync the container structure and sprites
      this.clientServices.representation.reconcile(sprite, world, entityId);

      // Apply transform (position, rotation, scale from GlobalTransform)
      // Note: RepresentationService handles internal offsets, RenderSystem handles world position.
      sprite.position.set(pos.x, pos.y);
      sprite.rotation = globalTransform.rotation;
      sprite.scale.set(globalTransform.scaleX, globalTransform.scaleY);

      const parentId = world.getParent(entityId);
      let zBase = pos.y;
      if (parentId !== undefined) {
        const parentPos = world.getComponent(parentId, 'VisualPosition');
        if (parentPos) {
          zBase = parentPos.y;
        }
      }

      // When an entity is in a bed, we must ensure they render on top of the bed sprite.
      // Since map objects like beds use their height as a zIndexOffset (e.g., 32),
      // the sleeping entity's base zIndex (0) would normally cause it to render behind the bed.
      // We resolve the bed's client entity ID (since InBed stores the server ID) and
      // dynamically inherit the bed's zIndexOffset + 1 to guarantee correct ordering.
      const inBed = world.getComponent(entityId, 'InBed');
      if (inBed?.bedEntityId != null) {
        const clientBedId = world.getClientEntityId(inBed.bedEntityId);
        const bedSprite =
          clientBedId != null ? world.getComponent(clientBedId, 'Sprite') : undefined;
        const bedZOffset = bedSprite?.zIndexOffset ?? 0;
        sprite.zIndex = zBase + bedZOffset + 1;
      } else {
        sprite.zIndex = zBase + spriteComp.zIndexOffset;
      }
    }

    return [];
  }
}
